/**
 * Pure builders for the My Testing Devices Settings tab — what the tab SHOWS (seeded from the device
 * record + its A/V readback) and what it SENDS (only the keys that changed, in the exact shape the
 * staff-api `staffProxyTestingUpdateDevice` allowlist accepts). Kept free of React so the wire shape
 * is unit-tested directly.
 *
 * Wire rules (scio3 DeviceSettingsTab / DeviceOpsScheduleField):
 *  - muted / externalStorage → top-level booleans;
 *  - orientation / scale → top-level GraphQL enum KEYS (UPPERCASE; the record stores lowercase values);
 *  - brightness / volume / lockdownPassword / playbackControl* / externalStoragePath / scheduleOps* →
 *    `feature.<key>` (open-api merges `feature` key-by-key, so a partial object only touches its keys).
 */
import type { DeviceLiveSettings } from './parse-device-info';
import type { TestingDeviceDetail } from './types';

export type SettingsPayload = Record<string, unknown> & { feature?: Record<string, unknown> };

export const ORIENTATION_OPTIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'rotate_90', label: 'Rotate 90°' },
  { value: 'rotate_180', label: 'Rotate 180°' },
  { value: 'rotate_270', label: 'Rotate 270°' },
] as const;

export const SCALE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'fit', label: 'Fit' },
  { value: 'stretch', label: 'Stretch' },
  { value: 'zoom', label: 'Zoom' },
] as const;

/** Volume shown when the device has neither a reported nor a stored value (scio3 #1805: not 100%). */
export const DEFAULT_VOLUME = 30;
export const DEFAULT_BRIGHTNESS = 100;
export const DEFAULT_PLAYBACK_TIMEOUT = 60;

/** Clamp a raw value to a 0–100 integer percent, or the fallback when unset / not a number. */
export function pct(raw: unknown, fallback: number): number {
  if (raw === null || raw === undefined || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function feat(detail: TestingDeviceDetail | null | undefined): Record<string, unknown> {
  return detail?.feature ?? {};
}

// ---------------------------------------------------------------------------
// Display — Mute · Brightness · Volume · Orientation · Scale
// ---------------------------------------------------------------------------

export interface DisplayValues {
  muted: boolean;
  brightness: number;
  volume: number;
  orientation: string;
  scale: string;
}

/**
 * What the Display controls start from: the device's ACTUAL A/V state when it reports one (live pull,
 * else the server-cached last pull), otherwise the stored record — scio3's seeding order.
 */
export function displayBaseline(
  detail: TestingDeviceDetail | null | undefined,
  reported: DeviceLiveSettings | undefined,
): DisplayValues {
  const f = feat(detail);
  return {
    muted: reported?.muted ?? Boolean(detail?.muted),
    brightness: pct(reported?.backlight ?? f.brightness, DEFAULT_BRIGHTNESS),
    volume: pct(reported?.volume ?? f.volume, DEFAULT_VOLUME),
    orientation: (detail?.orientation ?? '').toLowerCase(),
    scale: (detail?.scale ?? '').toLowerCase(),
  };
}

/** Only the changed Display keys, or null when nothing changed. */
export function buildDisplayPatch(
  baseline: DisplayValues,
  draft: Partial<DisplayValues>,
): SettingsPayload | null {
  const out: SettingsPayload = {};
  const feature: Record<string, unknown> = {};
  if (draft.muted !== undefined && draft.muted !== baseline.muted) out.muted = draft.muted;
  if (draft.orientation && draft.orientation !== baseline.orientation) {
    out.orientation = draft.orientation.toUpperCase();
  }
  if (draft.scale && draft.scale !== baseline.scale) out.scale = draft.scale.toUpperCase();
  if (draft.brightness !== undefined && draft.brightness !== baseline.brightness) {
    feature.brightness = pct(draft.brightness, baseline.brightness);
  }
  if (draft.volume !== undefined && draft.volume !== baseline.volume) {
    feature.volume = pct(draft.volume, baseline.volume);
  }
  if (Object.keys(feature).length) out.feature = feature;
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// Controls sub-config — playback control (the toggles themselves go through toggleDeviceFeature)
// ---------------------------------------------------------------------------

export interface PlaybackValues {
  timeout: number;
  autoplay: boolean;
  navBar: boolean;
}

export function playbackBaseline(detail: TestingDeviceDetail | null | undefined): PlaybackValues {
  const f = feat(detail);
  const n = Number(f.playbackControlTimeout);
  return {
    timeout: Number.isFinite(n) && n > 0 ? Math.round(n) : DEFAULT_PLAYBACK_TIMEOUT,
    autoplay: f.playbackControlAutoplay !== false,
    navBar: f.playbackControlNavBar === true,
  };
}

export function buildPlaybackPatch(
  baseline: PlaybackValues,
  draft: Partial<PlaybackValues>,
): SettingsPayload | null {
  const feature: Record<string, unknown> = {};
  if (draft.timeout !== undefined && draft.timeout !== baseline.timeout) {
    feature.playbackControlTimeout = Math.max(1, Math.round(draft.timeout));
  }
  if (draft.autoplay !== undefined && draft.autoplay !== baseline.autoplay) {
    feature.playbackControlAutoplay = draft.autoplay;
  }
  if (draft.navBar !== undefined && draft.navBar !== baseline.navBar) {
    feature.playbackControlNavBar = draft.navBar;
  }
  return Object.keys(feature).length ? { feature } : null;
}

/**
 * Unlock password — write-only. The tab never seeds the field from the record and clears it after a
 * save, so the value is never echoed back into UI state. Empty input → nothing to send.
 */
export function buildLockdownPasswordPatch(typed: string): SettingsPayload | null {
  const value = typed.trim();
  return value ? { feature: { lockdownPassword: value } } : null;
}

// ---------------------------------------------------------------------------
// Storage — external storage (utils-mdm only) + custom path
// ---------------------------------------------------------------------------

export interface StorageValues {
  enabled: boolean;
  path: string;
}

export function storageBaseline(detail: TestingDeviceDetail | null | undefined): StorageValues {
  const path = feat(detail).externalStoragePath;
  return { enabled: Boolean(detail?.externalStorage), path: typeof path === 'string' ? path : '' };
}

export function buildStoragePatch(
  baseline: StorageValues,
  draft: Partial<StorageValues>,
): SettingsPayload | null {
  const out: SettingsPayload = {};
  if (draft.enabled !== undefined && draft.enabled !== baseline.enabled) {
    out.externalStorage = draft.enabled;
  }
  if (draft.path !== undefined && draft.path.trim() !== baseline.path.trim()) {
    // Blank = auto-detect. The allowlist takes a string (never undefined), so blank is sent as ''.
    out.feature = { externalStoragePath: draft.path.trim() };
  }
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// Capability gates (scio3 DeviceSettingsTab / useDeviceCapabilities)
// ---------------------------------------------------------------------------

/** Settings tab: managed native hardware only (utils-mdm or builtin-mdm). */
export function supportsSettings(cap: string | null | undefined): boolean {
  return cap === 'utils-mdm' || cap === 'builtin-mdm';
}

/** External storage is a utils-mdm capability only (Electron / Yocto ProPlayer / Android). */
export function supportsExternalStorage(cap: string | null | undefined): boolean {
  return cap === 'utils-mdm';
}

/**
 * Operational schedule — scio3 `supportsOperationalSchedule`: not Roku, not tvOS, and on Tizen only
 * SSSP. scio3 reads `device.device` (the player type); the testing detail has no such field, so the
 * reported `platform` stands in for it.
 */
export function supportsOpsSchedule(detail: TestingDeviceDetail | null | undefined): boolean {
  const os = (detail?.os ?? '').toLowerCase();
  const kind = (detail?.platform ?? detail?.classification?.platform ?? '').toLowerCase();
  if (kind === 'roku' || os === 'roku') return false;
  if (os === 'tvos') return false;
  if (os === 'tizen' && kind !== 'sssp') return false;
  return true;
}
